import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  Badge,
  Progress,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  Input,
  Tooltip,
  Icon,
  useColorModeValue,
  Select,
  FormControl,
  FormLabel,
} from '@chakra-ui/react'
import {
  Clock,
  CheckCircle,
  Timer,
  ChartLine,
  Star,
  Warning,
  ChatDots,
  Hourglass,
  ArrowRight,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react'
import api from '../services/api'
import { useNavigate } from 'react-router-dom'
import IndicatorChatsModal, { type IndicatorKey } from '../components/IndicatorChatsModal'
import BotQualityPanel from '../components/BotQualityPanel'

function formatDurationPt(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const remS = s % 60
  if (m < 60) return remS ? `${m} min ${remS}s` : `${m} min`
  const h = Math.floor(m / 60)
  const remM = m % 60
  if (h < 24) return remM ? `${h}h ${remM}min` : `${h}h`
  const d = Math.floor(h / 24)
  const remH = h % 24
  return remH ? `${d}d ${remH}h` : `${d}d`
}

interface OverviewMetrics {
  totalChats: number
  pendingChats: number
  inProgressChats: number
  chatsByStatus: { status: string; count: number }[]
  avgFirstResponseMinutes: number | null
  avgResolutionMinutes: number | null
  chatsByCategory: { category: string; count: number }[]
  chatsByDay: { date: string; count: number }[]
  // Saúde da fila (ver metrics_controller.overview)
  closedBySystem?: number
  closedBySystemRate?: number | null
  aiHandled?: number
  aiOnlyResolved?: number
  aiHandoffRate?: number | null
  reopenedChats?: number
  chatsWithoutHumanResponse?: number
}

interface AgentMetric {
  agent: {
    id: number
    name: string
    email: string
    profilePic: string | null
  }
  completedChats: number
  currentlyAssigned: number
  avgFirstResponseMinutes: number | null
  avgFirstResponseBusinessMinutes: number | null
  avgResolutionMinutes: number | null
  messagesSent: number
  chatsWaitingResponse: number
  avgIdleMinutes: number | null
  csat: {
    average: number | null
    totalRatings: number
  }
}

interface SLAMetrics {
  firstResponse: { met: number; total: number; slaPercentage: string }
  resolution: { met: number; total: number; slaPercentage: string }
  pauses: { total: number; avgDurationMinutes: number | null; byStatus: { status: string; count: number }[] }
}

interface CriticalChat {
  id: number
  protocol: string
  userId: number
  userName: string
  agentName?: string | null
  categoryName: string
  status: string
  createdAt: string
  lastUserMessageAt: string | null
  lastAgentMessageAt: string | null
  /** Segundos desde criação (métricas críticas); legado: hoursOpen em horas */
  openSeconds?: number
  hoursOpen?: number
  unansweredUserMessages?: number
}

function chatOpenSeconds(chat: CriticalChat): number {
  if (chat.openSeconds != null) return Number(chat.openSeconds)
  if (chat.hoursOpen != null) return Number(chat.hoursOpen) * 3600
  return 0
}

function thresholdResolutionSeconds(t: CriticalChatsData['thresholds']): number {
  return (
    t.maxSecondsWithoutResolution ??
    (t.maxHoursWithoutResolution != null ? t.maxHoursWithoutResolution * 3600 : 24 * 3600)
  )
}

function thresholdStartSeconds(t: CriticalChatsData['thresholds']): number {
  return t.maxSecondsWithoutStart ?? (t.maxHoursWithoutStart != null ? t.maxHoursWithoutStart * 3600 : 4 * 3600)
}

interface PaginatedCritical {
  items: CriticalChat[]
  total: number
  page: number
  limit: number
}

interface CriticalChatsData {
  thresholds: {
    maxSecondsWithoutResolution?: number
    maxSecondsWithoutStart?: number
    /** Legado (API antes dos segundos) */
    maxHoursWithoutResolution?: number
    maxHoursWithoutStart?: number
    maxUserMessagesWithoutResponse: number
  }
  withoutResolution: PaginatedCritical
  withoutStart: PaginatedCritical
  manyUserMessages: PaginatedCritical
}

function StatCard({
  label,
  value,
  icon: IconComp,
  helpText,
  color = 'yellow.500',
  onVer,
}: {
  label: string
  value: string | number
  icon: React.ElementType
  helpText?: string
  color?: string
  /** Abre a lista de conversas por trás do número */
  onVer?: () => void
}) {
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  return (
    <Box bg={surfaceBg} p={6} borderRadius="xl">
      <HStack justify="space-between">
        <Stat>
          <StatLabel color={textMuted}>{label}</StatLabel>
          <StatNumber color={textPrimary} fontSize="3xl">
            {value}
          </StatNumber>
          {helpText && <StatHelpText color={textMuted}>{helpText}</StatHelpText>}
          {onVer && (
            <Button size="xs" variant="link" colorScheme="yellow" onClick={onVer} mt={1}>
              Ver conversas
            </Button>
          )}
        </Stat>
        <Box
          p={3}
          bg={color.replace('.500', '.100').replace('.400', '.100')}
          borderRadius="lg"
        >
          <IconComp size={28} color={color} />
        </Box>
      </HStack>
    </Box>
  )
}

function formatMinutes(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '-'
  if (minutes < 60) return `${Math.round(minutes)}min`
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  if (hours < 24) return `${hours}h ${mins}min`
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  return `${days}d ${remainingHours}h`
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function CsatStars({ rating, total }: { rating: number | null; total: number }) {
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  if (rating === null || total === 0) return <Text color={textMuted} fontSize="sm">-</Text>
  return (
    <Tooltip label={`${rating.toFixed(1)} / 5 (${total} avaliações)`}>
      <HStack spacing={1}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Icon
            key={star}
            as={Star}
            weight={star <= Math.round(rating) ? 'fill' : 'regular'}
            color={star <= Math.round(rating) ? 'yellow.400' : 'gray.600'}
            boxSize={4}
          />
        ))}
        <Text color={textMuted} fontSize="xs" ml={1}>({total})</Text>
      </HStack>
    </Tooltip>
  )
}

function PaginationBar({
  page,
  limit,
  total,
  onPrev,
  onNext,
  isLoading,
}: {
  page: number
  limit: number
  total: number
  onPrev: () => void
  onNext: () => void
  isLoading: boolean
}) {
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const start = total === 0 ? 0 : (page - 1) * limit + 1
  const end = Math.min(page * limit, total)
  const hasPrev = page > 1
  const hasNext = end < total
  return (
    <HStack justify="space-between" mt={3} flexWrap="wrap" gap={2}>
      <Text color={textMuted} fontSize="sm">
        {total === 0 ? 'Nenhum item' : `${start}–${end} de ${total}`}
      </Text>
      <HStack spacing={2}>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Icon as={CaretLeft} />}
          isDisabled={!hasPrev || isLoading}
          onClick={onPrev}
        >
          Anterior
        </Button>
        <Button
          size="sm"
          variant="outline"
          rightIcon={<Icon as={CaretRight} />}
          isDisabled={!hasNext || isLoading}
          onClick={onNext}
        >
          Próxima
        </Button>
      </HStack>
    </HStack>
  )
}

const PERIOD_PRESETS = [
  { label: '7d', days: 7 },
  { label: '15d', days: 15 },
  { label: '30d', days: 30 },
  { label: '60d', days: 60 },
  { label: '90d', days: 90 },
]

function toDateInputValue(date: Date): string {
  return date.toISOString().split('T')[0]
}

export default function Metrics() {
  const navigate = useNavigate()
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const [overview, setOverview] = useState<OverviewMetrics | null>(null)
  const [agents, setAgents] = useState<AgentMetric[]>([])
  const [sla, setSLA] = useState<SLAMetrics | null>(null)
  const [critical, setCritical] = useState<CriticalChatsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isCriticalLoading, setIsCriticalLoading] = useState(true)
  const [criticalPage, setCriticalPage] = useState({
    withoutResolution: 1,
    withoutStart: 1,
    manyUserMessages: 1,
  })
  const [criticalLimit, setCriticalLimit] = useState(20)
  const [criticalFilters, setCriticalFilters] = useState({
    categoryId: '' as string,
    status: '' as string,
    search: '' as string,
  })
  const [searchInputValue, setSearchInputValue] = useState('') // valor exibido no input (busca com debounce)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [categoriesForFilter, setCategoriesForFilter] = useState<{ id: number; name: string }[]>([])

  const [selectedPreset, setSelectedPreset] = useState<number | null>(30)
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return toDateInputValue(d)
  })
  const [endDate, setEndDate] = useState<string>(toDateInputValue(new Date()))

  const [erroCarga, setErroCarga] = useState<string | null>(null)
  const [indicadorAberto, setIndicadorAberto] = useState<IndicatorKey | null>(null)
  // Sequência das buscas: o input de data dispara onChange com valor vazio no
  // meio da digitação, e uma busca sem data (que o backend lia como "tudo")
  // chegava DEPOIS da filtrada e sobrescrevia os cartões. Só a última vale.
  const buscaSeqRef = useRef(0)

  const fetchMetrics = useCallback(async () => {
    const DATA_RE = /^\d{4}-\d{2}-\d{2}$/
    if (!DATA_RE.test(startDate) || !DATA_RE.test(endDate) || startDate > endDate) {
      return
    }
    const seq = ++buscaSeqRef.current
    setIsLoading(true)
    setErroCarga(null)
    try {
      const params = {
        startDate: `${startDate}T00:00:00`,
        endDate: `${endDate}T23:59:59`,
      }
      // Cada bloco atualiza sozinho: antes um Promise.all falhava inteiro se uma
      // das três chamadas caísse (ex.: durante um deploy) e a tela ficava com
      // os números do período anterior sem nenhum aviso.
      // Cada bloco aparece assim que a sua chamada volta (a de agentes é a mais
      // lenta e segurava a tela inteira). Busca mais nova descarta a antiga.
      const atual = () => seq === buscaSeqRef.current
      const [overviewRes, agentsRes, slaRes] = await Promise.allSettled([
        api.get('/admin/metrics/overview', { params }).then((r) => {
          if (atual()) setOverview(r.data)
        }),
        api.get('/admin/metrics/agents', { params }).then((r) => {
          if (atual()) setAgents(r.data.agents)
        }),
        api.get('/admin/metrics/sla', { params }).then((r) => {
          if (atual()) setSLA(r.data)
        }),
      ])
      if (!atual()) return
      const falhas: string[] = []
      if (overviewRes.status === 'rejected') falhas.push('visão geral')
      if (agentsRes.status === 'rejected') falhas.push('agentes')
      if (slaRes.status === 'rejected') falhas.push('SLA')
      if (falhas.length > 0) {
        console.error('Error fetching metrics:', { overviewRes, agentsRes, slaRes })
        setErroCarga(
          `Não foi possível atualizar: ${falhas.join(', ')}. Os números exibidos podem ser do período anterior — tente de novo.`
        )
      }
    } finally {
      if (seq === buscaSeqRef.current) setIsLoading(false)
    }
  }, [startDate, endDate])

  const periodoLabel = (() => {
    const fmt = (v: string) => {
      const [y, m, d] = v.split('-')
      return y && m && d ? `${d}/${m}/${y}` : v
    }
    return startDate && endDate ? `${fmt(startDate)} a ${fmt(endDate)}` : 'período selecionado'
  })()

  const fetchCritical = useCallback(async () => {
    setIsCriticalLoading(true)
    try {
      const params: Record<string, string | number> = {
        withoutResolutionPage: criticalPage.withoutResolution,
        withoutResolutionLimit: criticalLimit,
        withoutStartPage: criticalPage.withoutStart,
        withoutStartLimit: criticalLimit,
        manyUserMessagesPage: criticalPage.manyUserMessages,
        manyUserMessagesLimit: criticalLimit,
      }
      if (criticalFilters.categoryId) params.categoryId = criticalFilters.categoryId
      if (criticalFilters.status) params.status = criticalFilters.status
      if (criticalFilters.search.trim()) params.search = criticalFilters.search.trim()
      const res = await api.get('/admin/metrics/critical-chats', { params })
      setCritical(res.data)
    } catch (error) {
      console.error('Error fetching critical chats:', error)
    } finally {
      setIsCriticalLoading(false)
    }
  }, [
    criticalPage.withoutResolution,
    criticalPage.withoutStart,
    criticalPage.manyUserMessages,
    criticalLimit,
    criticalFilters.categoryId,
    criticalFilters.status,
    criticalFilters.search,
  ])

  const applyCriticalFilter = useCallback(
    (key: 'categoryId' | 'status' | 'search', value: string) => {
      setCriticalFilters((f) => ({ ...f, [key]: value }))
      setCriticalPage({ withoutResolution: 1, withoutStart: 1, manyUserMessages: 1 })
    },
    []
  )

  const applyCriticalLimit = useCallback((value: number) => {
    setCriticalLimit(value)
    setCriticalPage({ withoutResolution: 1, withoutStart: 1, manyUserMessages: 1 })
  }, [])

  const clearCriticalFilters = useCallback(() => {
    setCriticalFilters({ categoryId: '', status: '', search: '' })
    setSearchInputValue('')
    setCriticalPage({ withoutResolution: 1, withoutStart: 1, manyUserMessages: 1 })
  }, [])

  const handleCriticalSearchChange = useCallback(
    (value: string) => {
      setSearchInputValue(value)
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = setTimeout(() => {
        setCriticalFilters((f) => ({ ...f, search: value }))
        setCriticalPage({ withoutResolution: 1, withoutStart: 1, manyUserMessages: 1 })
        searchDebounceRef.current = null
      }, 400)
    },
    []
  )

  useEffect(() => {
    fetchMetrics()
  }, [fetchMetrics])

  useEffect(() => {
    fetchCritical()
  }, [fetchCritical])

  useEffect(() => {
    api
      .get('/admin/categories')
      .then((r) => setCategoriesForFilter(r.data.map((c: { id: number; name: string }) => ({ id: c.id, name: c.name }))))
      .catch(() => {})
  }, [])

  const handlePreset = (days: number) => {
    setSelectedPreset(days)
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - days)
    setStartDate(toDateInputValue(start))
    setEndDate(toDateInputValue(end))
  }

  const handleCustomDate = (field: 'start' | 'end', value: string) => {
    setSelectedPreset(null)
    if (field === 'start') setStartDate(value)
    else setEndDate(value)
  }

  const totalCritical = critical
    ? critical.withoutResolution.total + critical.withoutStart.total + critical.manyUserMessages.total
    : 0

  if (isLoading && !overview && isCriticalLoading) {
    return (
      <Box p={6} h="100%" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="yellow.500" />
      </Box>
    )
  }

  return (
    <Box p={6} h="100%" overflowY="auto">
      <VStack align="stretch" spacing={6}>
        {/* Header + Period Filter */}
        <VStack align="stretch" spacing={3}>
          <Text fontSize="2xl" fontWeight="bold" color={textPrimary}>
            Métricas e SLA
          </Text>
          <HStack spacing={2} flexWrap="wrap">
            {PERIOD_PRESETS.map((p) => (
              <Button
                key={p.days}
                size="sm"
                variant={selectedPreset === p.days ? 'solid' : 'outline'}
                colorScheme={selectedPreset === p.days ? 'yellow' : 'gray'}
                onClick={() => handlePreset(p.days)}
              >
                {p.label}
              </Button>
            ))}
            <HStack spacing={2} ml={2}>
              <Input
                type="date"
                size="sm"
                w="150px"
                bg={surfaceBg}
                borderColor={borderSubtle}
                value={startDate}
                onChange={(e) => handleCustomDate('start', e.target.value)}
              />
              <Text color={textMuted} fontSize="sm">até</Text>
              <Input
                type="date"
                size="sm"
                w="150px"
                bg={surfaceBg}
                borderColor={borderSubtle}
                value={endDate}
                onChange={(e) => handleCustomDate('end', e.target.value)}
              />
            </HStack>
          </HStack>
        </VStack>

        {erroCarga && (
          <Text color="red.300" fontSize="sm">
            {erroCarga}
          </Text>
        )}

        {/* Overview Cards */}
        {isLoading && !overview ? (
          <Box display="flex" justifyContent="center" py={10}>
            <Spinner size="lg" color="yellow.500" />
          </Box>
        ) : (
          <>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <StatCard
                label="Total de Atendimentos"
                value={overview?.totalChats || 0}
                icon={CheckCircle}
                helpText={`${periodoLabel} · ${overview?.pendingChats || 0} pendentes agora`}
                color="#48BB78"
              />
              <StatCard
                label="Em Andamento"
                value={overview?.inProgressChats || 0}
                icon={Clock}
                color="#4299E1"
              />
              <StatCard
                label="Tempo Médio 1ª Resposta Humana"
                value={formatMinutes(overview?.avgFirstResponseMinutes || null)}
                icon={Timer}
                helpText="Da entrada na fila humana até a 1ª mensagem de um atendente"
                color="#ECC94B"
              />
              <StatCard
                label="Tempo Médio Resolução"
                value={formatMinutes(overview?.avgResolutionMinutes || null)}
                icon={ChartLine}
                color="#9F7AEA"
              />
            </SimpleGrid>

            {/* Saúde da fila: onde a insatisfação se esconde (sem CSAT) */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <StatCard
                label="Encerrados pelo sistema"
                value={
                  overview?.closedBySystemRate != null
                    ? `${overview.closedBySystemRate}%`
                    : '-'
                }
                icon={Clock}
                helpText={`${overview?.closedBySystem || 0} chats por inatividade (sem avaliação)`}
                color="#F56565"
              />
              <StatCard
                label="IA → humano"
                value={
                  overview?.aiHandoffRate != null ? `${overview.aiHandoffRate}%` : '-'
                }
                icon={Timer}
                helpText={`${overview?.aiOnlyResolved || 0} resolvidos só pela IA de ${overview?.aiHandled || 0}`}
                color="#ED8936"
              />
              <StatCard
                label="Reabertos pelo cliente"
                value={overview?.reopenedChats || 0}
                icon={ChartLine}
                helpText="Voltaram a escrever após concluído"
                color="#ECC94B"
                onVer={() => setIndicadorAberto('reopened')}
              />
              <StatCard
                label="Sem resposta humana"
                value={overview?.chatsWithoutHumanResponse || 0}
                icon={CheckCircle}
                helpText="Nenhum atendente respondeu (exclui resolvidos pela IA)"
                color="#A0AEC0"
                onVer={() => setIndicadorAberto('no_human_response')}
              />
            </SimpleGrid>

            <IndicatorChatsModal
              isOpen={indicadorAberto !== null}
              onClose={() => setIndicadorAberto(null)}
              indicator={indicadorAberto}
              startDate={startDate}
              endDate={endDate}
            />

            <Tabs
              variant="soft-rounded"
              colorScheme="yellow"
              isLazy
              lazyBehavior="keepMounted"
            >
              <TabList>
                <Tab>Agentes</Tab>
                <Tab>SLA</Tab>
                <Tab>Por Categoria</Tab>
                <Tab>
                  Críticos
                  {totalCritical > 0 && (
                    <Badge ml={2} colorScheme="red" borderRadius="full" fontSize="xs">
                      {totalCritical}
                    </Badge>
                  )}
                </Tab>
                <Tab>Qualidade dos bots</Tab>
              </TabList>

              <TabPanels>
                {/* Agents Tab */}
                <TabPanel px={0}>
                  <Box bg={surfaceBg} borderRadius="xl" overflow="hidden" overflowX="auto">
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th color={textMuted}>Atendente</Th>
                          <Th color={textMuted} isNumeric>Concluídos</Th>
                          <Th color={textMuted} isNumeric>Em Andamento</Th>
                          <Th color={textMuted} isNumeric>
                            <Tooltip label="Tempo médio entre uma mensagem do cliente e a próxima resposta deste atendente, no mesmo chat (relógio). A espera do cliente na fila NÃO entra aqui — ela está no cartão 'Tempo Médio 1ª Resposta Humana'.">
                              <Text as="span">Resposta ao cliente</Text>
                            </Tooltip>
                          </Th>
                          <Th color={textMuted} isNumeric>
                            <Tooltip label="Mesma medida, contando só o horário de atendimento">
                              <Text as="span">Resp. ao cliente (útil)</Text>
                            </Tooltip>
                          </Th>
                          <Th color={textMuted} isNumeric>Resolução</Th>
                          <Th color={textMuted} isNumeric>Mensagens</Th>
                          <Th color={textMuted} isNumeric>
                            <Tooltip label="Conversas aguardando resposta do atendente">
                              <Text as="span">Aguardando</Text>
                            </Tooltip>
                          </Th>
                          <Th color={textMuted}>CSAT</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {agents.map((metric) => (
                          <Tr key={metric.agent.id}>
                            <Td>
                              <HStack>
                                <Avatar
                                  size="sm"
                                  name={metric.agent.name}
                                  src={metric.agent.profilePic || undefined}
                                />
                                <VStack align="start" spacing={0}>
                                  <Text color={textPrimary} fontSize="sm">
                                    {metric.agent.name}
                                  </Text>
                                  <Text color={textMuted} fontSize="xs">
                                    {metric.agent.email}
                                  </Text>
                                </VStack>
                              </HStack>
                            </Td>
                            <Td isNumeric>
                              <Badge colorScheme="green">{metric.completedChats}</Badge>
                            </Td>
                            <Td isNumeric>
                              <Badge colorScheme="blue">{metric.currentlyAssigned}</Badge>
                            </Td>
                            <Td isNumeric color={textSecondary} fontSize="sm">
                              {formatMinutes(metric.avgFirstResponseMinutes)}
                            </Td>
                            <Td isNumeric color={textSecondary} fontSize="sm">
                              {formatMinutes(metric.avgFirstResponseBusinessMinutes)}
                            </Td>
                            <Td isNumeric color={textSecondary} fontSize="sm">
                              {formatMinutes(metric.avgResolutionMinutes)}
                            </Td>
                            <Td isNumeric>
                              <Badge colorScheme="purple">{metric.messagesSent}</Badge>
                            </Td>
                            <Td isNumeric>
                              {metric.chatsWaitingResponse > 0 ? (
                                <Tooltip label={`Tempo médio aguardando: ${formatMinutes(metric.avgIdleMinutes)}`}>
                                  <Badge colorScheme="orange">{metric.chatsWaitingResponse}</Badge>
                                </Tooltip>
                              ) : (
                                <Badge colorScheme="green">0</Badge>
                              )}
                            </Td>
                            <Td>
                              <CsatStars rating={metric.csat.average} total={metric.csat.totalRatings} />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>

                {/* SLA Tab */}
                <TabPanel px={0}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box bg={surfaceBg} p={6} borderRadius="xl">
                      <VStack align="stretch" spacing={4}>
                        <Text fontWeight="semibold" color={textPrimary}>
                          SLA de Primeira Resposta
                        </Text>
                        <HStack justify="space-between">
                          <Text color={textMuted}>Meta: 1 hora</Text>
                          <Text
                            fontWeight="bold"
                            color={
                              parseFloat(sla?.firstResponse.slaPercentage || '0') >= 80
                                ? 'green.400'
                                : 'red.400'
                            }
                          >
                            {sla?.firstResponse.slaPercentage}%
                          </Text>
                        </HStack>
                        <Progress
                          value={parseFloat(sla?.firstResponse.slaPercentage || '0')}
                          colorScheme={
                            parseFloat(sla?.firstResponse.slaPercentage || '0') >= 80
                              ? 'green'
                              : 'red'
                          }
                          borderRadius="full"
                        />
                        <Text fontSize="sm" color={textMuted}>
                          {sla?.firstResponse.met} de {sla?.firstResponse.total} dentro da meta
                        </Text>
                      </VStack>
                    </Box>

                    <Box bg={surfaceBg} p={6} borderRadius="xl">
                      <VStack align="stretch" spacing={4}>
                        <Text fontWeight="semibold" color={textPrimary}>
                          SLA de Resolução
                        </Text>
                        <HStack justify="space-between">
                          <Text color={textMuted}>Meta: 24 horas</Text>
                          <Text
                            fontWeight="bold"
                            color={
                              parseFloat(sla?.resolution.slaPercentage || '0') >= 80
                                ? 'green.400'
                                : 'red.400'
                            }
                          >
                            {sla?.resolution.slaPercentage}%
                          </Text>
                        </HStack>
                        <Progress
                          value={parseFloat(sla?.resolution.slaPercentage || '0')}
                          colorScheme={
                            parseFloat(sla?.resolution.slaPercentage || '0') >= 80
                              ? 'green'
                              : 'red'
                          }
                          borderRadius="full"
                        />
                        <Text fontSize="sm" color={textMuted}>
                          {sla?.resolution.met} de {sla?.resolution.total} dentro da meta
                        </Text>
                      </VStack>
                    </Box>

                    <Box bg={surfaceBg} p={6} borderRadius="xl" gridColumn={{ md: 'span 2' }}>
                      <VStack align="stretch" spacing={4}>
                        <Text fontWeight="semibold" color={textPrimary}>
                          Pausas de SLA
                        </Text>
                        <SimpleGrid columns={3} spacing={4}>
                          <Stat>
                            <StatLabel color={textMuted}>Total de Pausas</StatLabel>
                            <StatNumber color={textPrimary}>{sla?.pauses.total}</StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel color={textMuted}>Duração Média</StatLabel>
                            <StatNumber color={textPrimary}>
                              {formatMinutes(sla?.pauses.avgDurationMinutes || null)}
                            </StatNumber>
                          </Stat>
                        </SimpleGrid>
                        {sla?.pauses.byStatus && sla.pauses.byStatus.length > 0 && (
                          <VStack align="stretch" spacing={2}>
                            <Text fontSize="sm" color={textMuted}>
                              Por Status:
                            </Text>
                            <HStack flexWrap="wrap" gap={2}>
                              {sla.pauses.byStatus.map((item) => (
                                <Badge key={item.status} colorScheme="gray" px={3} py={1}>
                                  {item.status}: {item.count}
                                </Badge>
                              ))}
                            </HStack>
                          </VStack>
                        )}
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </TabPanel>

                {/* By Category Tab */}
                <TabPanel px={0}>
                  <Box bg={surfaceBg} borderRadius="xl" p={6}>
                    <VStack align="stretch" spacing={4}>
                      {overview?.chatsByCategory.map((cat) => (
                        <HStack key={cat.category} justify="space-between">
                          <Text color={textPrimary}>{cat.category}</Text>
                          <HStack>
                            <Progress
                              value={
                                (cat.count / (overview?.totalChats || 1)) * 100
                              }
                              w="200px"
                              colorScheme="yellow"
                              borderRadius="full"
                            />
                            <Badge colorScheme="blue" minW="50px" textAlign="center">
                              {cat.count}
                            </Badge>
                          </HStack>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </TabPanel>

                {/* Critical Chats Tab */}
                <TabPanel px={0}>
                  <Box mb={4} p={4} bg={surfaceBg} borderRadius="xl">
                    <HStack flexWrap="wrap" gap={4} align="flex-end">
                      <FormControl w="auto" minW="140px">
                        <FormLabel fontSize="xs" color={textMuted} mb={1}>
                          Itens por página
                        </FormLabel>
                        <Select
                          size="sm"
                          value={criticalLimit}
                          onChange={(e) => applyCriticalLimit(Number(e.target.value))}
                          bg={surfaceBg}
                          borderColor={borderSubtle}
                          w="100px"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </Select>
                      </FormControl>
                      <FormControl w="auto" minW="160px">
                        <FormLabel fontSize="xs" color={textMuted} mb={1}>
                          Categoria
                        </FormLabel>
                        <Select
                          size="sm"
                          value={criticalFilters.categoryId}
                          onChange={(e) => applyCriticalFilter('categoryId', e.target.value)}
                          bg={surfaceBg}
                          borderColor={borderSubtle}
                        >
                          <option value="">Todas</option>
                          {categoriesForFilter.map((c) => (
                            <option key={c.id} value={String(c.id)}>
                              {c.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl w="auto" minW="140px">
                        <FormLabel fontSize="xs" color={textMuted} mb={1}>
                          Status
                        </FormLabel>
                        <Select
                          size="sm"
                          value={criticalFilters.status}
                          onChange={(e) => applyCriticalFilter('status', e.target.value)}
                          bg={surfaceBg}
                          borderColor={borderSubtle}
                        >
                          <option value="">Todos</option>
                          <option value="pending">Pendente</option>
                          <option value="in_progress">Em andamento</option>
                        </Select>
                      </FormControl>
                      <FormControl flex={1} minW="180px" maxW="280px">
                        <FormLabel fontSize="xs" color={textMuted} mb={1}>
                          Busca (protocolo ou usuário)
                        </FormLabel>
                        <Input
                          size="sm"
                          placeholder="Digite para filtrar..."
                          value={searchInputValue}
                          onChange={(e) => handleCriticalSearchChange(e.target.value)}
                          bg={surfaceBg}
                          borderColor={borderSubtle}
                        />
                      </FormControl>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="gray"
                        onClick={clearCriticalFilters}
                        isDisabled={
                          !criticalFilters.categoryId &&
                          !criticalFilters.status &&
                          !criticalFilters.search.trim() &&
                          !searchInputValue.trim()
                        }
                      >
                        Limpar filtros
                      </Button>
                    </HStack>
                  </Box>
                  {isCriticalLoading ? (
                    <Box display="flex" justifyContent="center" py={10}>
                      <Spinner size="lg" color="yellow.500" />
                    </Box>
                  ) : !critical ? (
                    <Text color={textMuted}>Nenhum dado disponível</Text>
                  ) : (
                    <VStack align="stretch" spacing={6}>
                      {/* Without Resolution */}
                      <Box bg={surfaceBg} borderRadius="xl" p={6}>
                        <HStack mb={4} spacing={3}>
                          <Box p={2} bg="red.900" borderRadius="lg">
                            <Warning size={20} color="#FC8181" />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="semibold" color={textPrimary}>
                              Sem resolução há muito tempo
                            </Text>
                            <Text fontSize="xs" color={textMuted}>
                              Abertos há mais de {formatDurationPt(thresholdResolutionSeconds(critical.thresholds))} sem resolução
                            </Text>
                          </VStack>
                          <Badge colorScheme="red" ml="auto">{critical.withoutResolution.total}</Badge>
                        </HStack>
                        {critical.withoutResolution.total === 0 ? (
                          <Text color={textMuted} fontSize="sm">Nenhum chamado crítico nesta categoria</Text>
                        ) : (
                          <>
                          <Box overflowX="auto">
                            <Table size="sm">
                              <Thead>
                                <Tr>
                                  <Th color={textMuted}>Protocolo</Th>
                                  <Th color={textMuted}>Usuário</Th>
                                  <Th color={textMuted}>Atendente</Th>
                                  <Th color={textMuted}>Categoria</Th>
                                  <Th color={textMuted} isNumeric>Tempo aberto</Th>
                                  <Th color={textMuted}>Status</Th>
                                  <Th color={textMuted}></Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {critical.withoutResolution.items.map((chat) => (
                                  <Tr key={chat.id}>
                                    <Td><Text color={textPrimary} fontSize="sm" fontFamily="mono">{chat.protocol || `#${chat.id}`}</Text></Td>
                                    <Td><Text color={textSecondary} fontSize="sm">{chat.userName}</Text></Td>
                                    <Td><Text color={textSecondary} fontSize="sm">{chat.agentName || '-'}</Text></Td>
                                    <Td><Badge colorScheme="gray" fontSize="xs">{chat.categoryName}</Badge></Td>
                                    <Td isNumeric><Badge colorScheme="red">{formatDurationPt(chatOpenSeconds(chat))}</Badge></Td>
                                    <Td>
                                      <Badge colorScheme={chat.status === 'pending' ? 'yellow' : 'blue'} fontSize="xs">
                                        {chat.status === 'pending' ? 'Pendente' : 'Em andamento'}
                                      </Badge>
                                    </Td>
                                    <Td>
                                      <Tooltip label="Abrir conversa">
                                        <Button size="xs" variant="ghost" colorScheme="yellow" onClick={() => navigate(`/all-chats/${chat.id}`)}>
                                          <ArrowRight size={14} />
                                        </Button>
                                      </Tooltip>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                          <PaginationBar
                            page={critical.withoutResolution.page}
                            limit={critical.withoutResolution.limit}
                            total={critical.withoutResolution.total}
                            onPrev={() => setCriticalPage((p) => ({ ...p, withoutResolution: p.withoutResolution - 1 }))}
                            onNext={() => setCriticalPage((p) => ({ ...p, withoutResolution: p.withoutResolution + 1 }))}
                            isLoading={isCriticalLoading}
                          />
                        </>
                        )}
                      </Box>

                      {/* Without Start */}
                      <Box bg={surfaceBg} borderRadius="xl" p={6}>
                        <HStack mb={4} spacing={3}>
                          <Box p={2} bg="orange.900" borderRadius="lg">
                            <Hourglass size={20} color="#F6AD55" />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="semibold" color={textPrimary}>
                              Sem início há muito tempo
                            </Text>
                            <Text fontSize="xs" color={textMuted}>
                              Pendentes há mais de {formatDurationPt(thresholdStartSeconds(critical.thresholds))} sem atendente
                            </Text>
                          </VStack>
                          <Badge colorScheme="orange" ml="auto">{critical.withoutStart.total}</Badge>
                        </HStack>
                        {critical.withoutStart.total === 0 ? (
                          <Text color={textMuted} fontSize="sm">Nenhum chamado crítico nesta categoria</Text>
                        ) : (
                          <>
                          <Box overflowX="auto">
                            <Table size="sm">
                              <Thead>
                                <Tr>
                                  <Th color={textMuted}>Protocolo</Th>
                                  <Th color={textMuted}>Usuário</Th>
                                  <Th color={textMuted}>Categoria</Th>
                                  <Th color={textMuted} isNumeric>Tempo aberto</Th>
                                  <Th color={textMuted}>Criado em</Th>
                                  <Th color={textMuted}></Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {critical.withoutStart.items.map((chat) => (
                                  <Tr key={chat.id}>
                                    <Td><Text color={textPrimary} fontSize="sm" fontFamily="mono">{chat.protocol || `#${chat.id}`}</Text></Td>
                                    <Td><Text color={textSecondary} fontSize="sm">{chat.userName}</Text></Td>
                                    <Td><Badge colorScheme="gray" fontSize="xs">{chat.categoryName}</Badge></Td>
                                    <Td isNumeric><Badge colorScheme="orange">{formatDurationPt(chatOpenSeconds(chat))}</Badge></Td>
                                    <Td><Text color={textMuted} fontSize="xs">{formatDate(chat.createdAt)}</Text></Td>
                                    <Td>
                                      <Tooltip label="Abrir conversa">
                                        <Button size="xs" variant="ghost" colorScheme="yellow" onClick={() => navigate(`/all-chats/${chat.id}`)}>
                                          <ArrowRight size={14} />
                                        </Button>
                                      </Tooltip>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                          <PaginationBar
                            page={critical.withoutStart.page}
                            limit={critical.withoutStart.limit}
                            total={critical.withoutStart.total}
                            onPrev={() => setCriticalPage((p) => ({ ...p, withoutStart: p.withoutStart - 1 }))}
                            onNext={() => setCriticalPage((p) => ({ ...p, withoutStart: p.withoutStart + 1 }))}
                            isLoading={isCriticalLoading}
                          />
                        </>
                        )}
                      </Box>

                      {/* Many User Messages */}
                      <Box bg={surfaceBg} borderRadius="xl" p={6}>
                        <HStack mb={4} spacing={3}>
                          <Box p={2} bg="purple.900" borderRadius="lg">
                            <ChatDots size={20} color="#B794F4" />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="semibold" color={textPrimary}>
                              Muitas mensagens sem resposta
                            </Text>
                            <Text fontSize="xs" color={textMuted}>
                              Mais de {critical.thresholds.maxUserMessagesWithoutResponse} mensagens do usuário sem resposta do atendente
                            </Text>
                          </VStack>
                          <Badge colorScheme="purple" ml="auto">{critical.manyUserMessages.total}</Badge>
                        </HStack>
                        {critical.manyUserMessages.total === 0 ? (
                          <Text color={textMuted} fontSize="sm">Nenhum chamado crítico nesta categoria</Text>
                        ) : (
                          <>
                          <Box overflowX="auto">
                            <Table size="sm">
                              <Thead>
                                <Tr>
                                  <Th color={textMuted}>Protocolo</Th>
                                  <Th color={textMuted}>Usuário</Th>
                                  <Th color={textMuted}>Atendente</Th>
                                  <Th color={textMuted}>Categoria</Th>
                                  <Th color={textMuted} isNumeric>Msgs sem resp.</Th>
                                  <Th color={textMuted}>Última msg usuário</Th>
                                  <Th color={textMuted}></Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {critical.manyUserMessages.items.map((chat) => (
                                  <Tr key={chat.id}>
                                    <Td><Text color={textPrimary} fontSize="sm" fontFamily="mono">{chat.protocol || `#${chat.id}`}</Text></Td>
                                    <Td><Text color={textSecondary} fontSize="sm">{chat.userName}</Text></Td>
                                    <Td><Text color={textSecondary} fontSize="sm">{chat.agentName || '-'}</Text></Td>
                                    <Td><Badge colorScheme="gray" fontSize="xs">{chat.categoryName}</Badge></Td>
                                    <Td isNumeric><Badge colorScheme="purple">{chat.unansweredUserMessages}</Badge></Td>
                                    <Td><Text color={textMuted} fontSize="xs">{formatDate(chat.lastUserMessageAt || '')}</Text></Td>
                                    <Td>
                                      <Tooltip label="Abrir conversa">
                                        <Button size="xs" variant="ghost" colorScheme="yellow" onClick={() => navigate(`/all-chats/${chat.id}`)}>
                                          <ArrowRight size={14} />
                                        </Button>
                                      </Tooltip>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                          <PaginationBar
                            page={critical.manyUserMessages.page}
                            limit={critical.manyUserMessages.limit}
                            total={critical.manyUserMessages.total}
                            onPrev={() => setCriticalPage((p) => ({ ...p, manyUserMessages: p.manyUserMessages - 1 }))}
                            onNext={() => setCriticalPage((p) => ({ ...p, manyUserMessages: p.manyUserMessages + 1 }))}
                            isLoading={isCriticalLoading}
                          />
                        </>
                        )}
                      </Box>
                    </VStack>
                  )}
                </TabPanel>

                {/* Qualidade dos bots (juiz de IA) */}
                <TabPanel px={0}>
                  <BotQualityPanel startDate={startDate} endDate={endDate} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </>
        )}
      </VStack>
    </Box>
  )
}
